import { VideoProject } from "@/services/bedrock";

const FLAG_NAV_PREFIX = "flag-";
const LAST_FAIL_NAV_PREFIX = "last-fail-";

export function focusQaIssueCard(issueId: string) {
  const card = document.querySelector<HTMLElement>(`[data-qa-issue-id="${issueId}"]`);
  if (!card) return;
  card.focus({ preventScroll: true });
  card.scrollIntoView({ block: "nearest", inline: "nearest" });
}

export function flagQaIssueNavKey(id: string): string {
  return `${FLAG_NAV_PREFIX}${id}`;
}

export function lastFailQaIssueNavKey(id: number): string {
  return `${LAST_FAIL_NAV_PREFIX}${id}`;
}

export function selectedQaIssueNavKey(
  selectedFlagId: string | null | undefined,
  selectedLastFailId: number | null | undefined
): string | null {
  if (selectedFlagId) return flagQaIssueNavKey(selectedFlagId);
  if (selectedLastFailId != null) return lastFailQaIssueNavKey(selectedLastFailId);
  return null;
}

export function parseQaIssueNavKey(
  key: string
): { kind: "flag"; id: string } | { kind: "last-fail"; id: number } | null {
  if (key.startsWith(FLAG_NAV_PREFIX)) return { kind: "flag", id: key.slice(FLAG_NAV_PREFIX.length) };
  if (key.startsWith(LAST_FAIL_NAV_PREFIX)) {
    return { kind: "last-fail", id: Number(key.slice(LAST_FAIL_NAV_PREFIX.length)) };
  }
  return null;
}

export function neighborQaIssueKey(
  keys: string[],
  currentKey: string | null | undefined,
  direction: -1 | 1
): string | undefined {
  if (!keys.length) return undefined;
  if (currentKey == null) return direction === 1 ? keys[0] : keys[keys.length - 1];
  const index = keys.indexOf(currentKey);
  if (index < 0) return direction === 1 ? keys[0] : keys[keys.length - 1];
  return keys[(index + direction + keys.length) % keys.length];
}

export interface QaFlag {
  id: string;
  category: VideoProject.QaIssueCategory;
  comment: string;
  timestampMs?: number | null;
}

export function neighborAfterRemove<T>(items: T[], removedIndex: number): T | undefined {
  if (removedIndex < 0) return undefined;
  return items[removedIndex + 1] ?? items[removedIndex - 1];
}

export function createQaFlagId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `qa-flag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function buildQaIssues(flags: QaFlag[]): VideoProject.QaIssue[] {
  return flags.map(flag => {
    const comment = flag.comment.trim();
    return {
      category: flag.category,
      comment: comment || null,
      timestampMs: flag.timestampMs ?? null,
    };
  });
}

export function formatQaTimestamp(timestampMs?: number | null): string | null {
  if (timestampMs == null || Number.isNaN(timestampMs)) return null;
  const totalSeconds = Math.max(0, Math.floor(timestampMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export const QA_TIMESTAMP_NUDGE_MS = 1000;

export function clampQaTimestampMs(timestampMs: number, durationSeconds: number): number {
  if (!Number.isFinite(timestampMs)) return 0;
  const rounded = Math.max(0, Math.round(timestampMs));
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return rounded;
  return Math.min(rounded, Math.round(durationSeconds * 1000));
}

export function timestampMsFromTimelineX(
  clientX: number,
  rect: Pick<DOMRect, "left" | "width">,
  durationSeconds: number
): number {
  if (!rect.width) return 0;
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  return clampQaTimestampMs(ratio * durationSeconds * 1000, durationSeconds);
}

export function nudgeQaTimestampMs(
  timestampMs: number | null | undefined,
  deltaMs: number,
  durationSeconds: number
): number {
  return clampQaTimestampMs((timestampMs ?? 0) + deltaMs, durationSeconds);
}

export function parseQaTimestamp(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const clock = trimmed.match(/^(\d+):([0-5]?\d)$/);
  if (clock) return (Number(clock[1]) * 60 + Number(clock[2])) * 1000;
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  return null;
}

export type LastFailResolution = "RESOLVED" | "UNRESOLVED";

export function lastFailTimestampMs(
  issue: Pick<VideoProject.QaAuditIssue, "id" | "timestampMs">,
  overrides: Record<number, number | null | undefined>
): number | null {
  if (Object.prototype.hasOwnProperty.call(overrides, issue.id)) {
    return overrides[issue.id] ?? null;
  }
  return issue.timestampMs ?? null;
}

function hasQaTimestamp(timestampMs?: number | null): timestampMs is number {
  return timestampMs != null && !Number.isNaN(timestampMs);
}

function sortByQaTimestamp<T extends { timestampMs?: number | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aTimestamp = a.timestampMs;
    const bTimestamp = b.timestampMs;
    const aHasTimestamp = hasQaTimestamp(aTimestamp);
    const bHasTimestamp = hasQaTimestamp(bTimestamp);
    if (aHasTimestamp !== bHasTimestamp) return aHasTimestamp ? 1 : -1;
    if (!aHasTimestamp || !bHasTimestamp) return 0;
    return aTimestamp - bTimestamp;
  });
}

type OrderedFlaggedIssue =
  | { kind: "prior"; issue: VideoProject.QaAuditIssue; timestampMs: number | null }
  | { kind: "flag"; flag: QaFlag; timestampMs: number | null };

export function orderedFlaggedIssues(
  priorIssues: VideoProject.QaAuditIssue[],
  flags: QaFlag[],
  timestampOverrides: Record<number, number | null | undefined> = {}
): OrderedFlaggedIssue[] {
  return sortByQaTimestamp([
    ...priorIssues.map(issue => ({
      kind: "prior" as const,
      issue,
      timestampMs: lastFailTimestampMs(issue, timestampOverrides),
    })),
    ...flags.map(flag => ({
      kind: "flag" as const,
      flag,
      timestampMs: flag.timestampMs ?? null,
    })),
  ]);
}

export function qaIssueNavKeys(
  priorIssues: Array<Pick<VideoProject.QaAuditIssue, "id" | "timestampMs">>,
  flags: Array<Pick<QaFlag, "id" | "timestampMs">> = [],
  timestampOverrides: Record<number, number | null | undefined> = {}
): string[] {
  return sortByQaTimestamp([
    ...priorIssues.map(issue => ({
      key: lastFailQaIssueNavKey(issue.id),
      timestampMs: lastFailTimestampMs(issue, timestampOverrides),
    })),
    ...flags.map(flag => ({
      key: flagQaIssueNavKey(flag.id),
      timestampMs: flag.timestampMs ?? null,
    })),
  ]).map(item => item.key);
}

export function lastFailComment(
  issue: Pick<VideoProject.QaAuditIssue, "id" | "comment">,
  overrides: Record<number, string | undefined>
): string {
  return overrides[issue.id] ?? issue.comment ?? "";
}

export function unmarkedLastFailIssues(
  issues: VideoProject.QaAuditIssue[] | undefined,
  resolutions: Record<number, LastFailResolution | undefined>
): VideoProject.QaAuditIssue[] {
  return (issues || []).filter(issue => resolutions[issue.id] == null);
}

export function unresolvedLastFailIssues(
  issues: VideoProject.QaAuditIssue[] | undefined,
  resolutions: Record<number, LastFailResolution | undefined>,
  timestampOverrides: Record<number, number | null | undefined> = {},
  commentOverrides: Record<number, string | undefined> = {}
): VideoProject.QaIssue[] {
  return (issues || [])
    .filter(issue => resolutions[issue.id] === "UNRESOLVED")
    .map(issue => ({
      category: issue.category,
      comment: lastFailComment(issue, commentOverrides).trim() || null,
      timestampMs: lastFailTimestampMs(issue, timestampOverrides),
    }));
}
